<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import type { LifecycleTone } from '../../constants/listingRequestLifecycle'

// The request-detail screen's soft status pill: a -50 tint with -700 text, an
// optional leading dot, and an optional hairline for the one pill (the header's
// stage) that has to hold its own against a button beside it. Chip's palette is
// solid-or-outline only, so this lives with the screen that needs the third
// look — the same recipe as the list's ListingStageStatus pill. The `icon` slot
// takes the dot's place for the few pills that carry a glyph ("Matched with DLD").
withDefaults(
  defineProps<{
    label: string
    tone?: LifecycleTone
    dot?: boolean
    bordered?: boolean
    size?: 'small' | 'medium'
  }>(),
  { tone: 'neutral', dot: false, bordered: false, size: 'small' },
)
</script>

<template>
  <Box
    is="span"
    flex
    align="center"
    :class="['pill', `pill--${tone}`, `pill--${size}`, { 'pill--bordered': bordered }]"
  >
    <slot name="icon">
      <Box v-if="dot" is="span" class="pill__dot" aria-hidden="true" />
    </slot>
    <Text
      is="span"
      :type="size === 'medium' ? 'body-sm' : 'caption'"
      :weight="size === 'medium' ? 'semibold' : 'medium'"
      color="inherit"
    >
      {{ label }}
    </Text>
  </Box>
</template>

<style scoped>
.pill--neutral {
  --tone-bg: var(--p-surface-100);
  --tone-border: var(--p-content-border-color);
  --tone-fg: var(--p-text-muted-color);
  --tone-solid: var(--p-surface-400);
}

.pill--primary {
  --tone-bg: var(--p-primary-50);
  --tone-border: var(--p-primary-200);
  --tone-fg: var(--p-primary-700);
  --tone-solid: var(--p-primary-500);
}

.pill--success {
  --tone-bg: var(--p-success-50);
  --tone-border: var(--p-success-200);
  --tone-fg: var(--p-success-700);
  --tone-solid: var(--p-success-500);
}

.pill--warning {
  --tone-bg: var(--p-warning-50);
  --tone-border: var(--p-warning-200);
  --tone-fg: var(--p-warning-700);
  --tone-solid: var(--p-warning-500);
}

.pill--danger {
  --tone-bg: var(--p-danger-50);
  --tone-border: var(--p-danger-200);
  --tone-fg: var(--p-danger-700);
  --tone-solid: var(--p-danger-500);
}

.pill--info {
  --tone-bg: var(--p-info-50);
  --tone-border: var(--p-info-200);
  --tone-fg: var(--p-info-700);
  --tone-solid: var(--p-info-500);
}

/* The -700 text steps are light-mode values; on the dark -50 grounds they lose
   contrast, so dark mode lifts the label a few steps (as ListingStageStatus does). */
:global(.p-dark) .pill--primary {
  --tone-fg: var(--p-primary-300);
}

:global(.p-dark) .pill--success {
  --tone-fg: var(--p-success-300);
}

:global(.p-dark) .pill--warning {
  --tone-fg: var(--p-warning-400);
}

:global(.p-dark) .pill--danger {
  --tone-fg: var(--p-danger-300);
}

:global(.p-dark) .pill--info {
  --tone-fg: var(--p-info-300);
}

.pill {
  flex-shrink: 0;
  gap: 0.375rem;
  border-radius: 999px;
  background: var(--tone-bg);
  color: var(--tone-fg);
  white-space: nowrap;
}

.pill--small {
  padding: 0.1875rem 0.6875rem 0.1875rem 0.5625rem;
}

.pill--medium {
  padding: 0.4375rem 0.875rem 0.4375rem 0.75rem;
  gap: 0.4375rem;
}

.pill--bordered {
  border: 1px solid var(--tone-border);
}

.pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--tone-solid);
  flex-shrink: 0;
}

.pill--medium .pill__dot {
  width: 8px;
  height: 8px;
}
</style>
